import { useMemo, useState } from 'react';
import type { LessonLog, TestRecord } from '@/types';
import LessonCalendar, {
  lessonDateKey,
  type MonthCursor,
} from '@/components/feature/LessonCalendar';
import ParentLessonList from './ParentLessonList';

interface ParentLessonSectionProps {
  lessons: LessonLog[];
  testsByLesson: Record<string, TestRecord[]>;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function formatSelectedDate(dateKey: string): string {
  const d = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateKey;
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 (${WEEKDAYS[d.getDay()]})`;
}

export default function ParentLessonSection({ lessons, testsByLesson }: ParentLessonSectionProps) {
  const initialDate = lessons.length > 0 ? lessonDateKey(lessons[0].lesson_date) : null;

  const [selectedDate, setSelectedDate] = useState<string | null>(initialDate);
  const [cursor, setCursor] = useState<MonthCursor>(() => {
    const base = lessons.length > 0 ? new Date(lessons[0].lesson_date) : new Date();
    if (Number.isNaN(base.getTime())) {
      const now = new Date();
      return { year: now.getFullYear(), month: now.getMonth() };
    }
    return { year: base.getFullYear(), month: base.getMonth() };
  });

  const selectedLessons = useMemo(
    () =>
      selectedDate ? lessons.filter((l) => lessonDateKey(l.lesson_date) === selectedDate) : [],
    [lessons, selectedDate],
  );

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
          <i className="ri-book-2-line"></i>
        </span>
        수업일지
        <span className="rounded-full bg-background-200 px-2.5 py-0.5 text-[11px] font-semibold text-foreground-600">
          {lessons.length}건
        </span>
      </h2>
      <p className="mt-1.5 text-xs text-foreground-500">
        달력에서 수업한 날을 누르면 그날의 수업일지를 확인할 수 있어요.
      </p>

      {lessons.length === 0 ? (
        <div className="mt-4">
          <ParentLessonList
            lessons={lessons}
            testsByLesson={testsByLesson}
            emptyMessage="아직 등록된 수업일지가 없어요."
          />
        </div>
      ) : (
        <div className="mt-4">
          <LessonCalendar
            lessons={lessons}
            cursor={cursor}
            onCursorChange={setCursor}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
          />

          <div className="mt-4">
            <h3 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
              <i className="ri-calendar-check-line text-primary-600"></i>
              {selectedDate ? formatSelectedDate(selectedDate) : '날짜를 선택해주세요'}
            </h3>
            <div className="mt-3">
              <ParentLessonList
                lessons={selectedLessons}
                testsByLesson={testsByLesson}
                emptyMessage="선택한 날짜에는 수업일지가 없어요. 달력에서 색이 표시된 날을 눌러보세요."
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}